/**
 * Snapshot a Losi mark’s SVG for the games morph.
 * Rewrites mask/ids so the clone doesn’t collide with the live avatar.
 */
import type { LosiGamesOpenOrigin } from '@/lib/losi-games/types'

export function originFromLosiElement(el: HTMLElement): LosiGamesOpenOrigin {
  const rect = el.getBoundingClientRect()
  const cx = rect.left + rect.width / 2
  const cy = rect.top + rect.height / 2
  const x = typeof window !== 'undefined' ? cx / window.innerWidth : 0.5
  const y = typeof window !== 'undefined' ? cy / window.innerHeight : 0.4
  const base: LosiGamesOpenOrigin = {
    x,
    y,
    cx,
    cy,
    w: Math.max(rect.width, 8),
    h: Math.max(rect.height, 8)
  }

  const svg = el.querySelector('svg') as SVGSVGElement | null
  if (!svg || rect.width < 8 || rect.height < 8) return base

  const clone = svg.cloneNode(true) as SVGSVGElement
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  clone.removeAttribute('aria-label')
  clone.setAttribute('aria-hidden', 'true')

  const uid = `lgm${Date.now().toString(36)}`
  const idMap = new Map<string, string>()
  clone.querySelectorAll('[id]').forEach((node) => {
    const old = node.getAttribute('id')
    if (!old) return
    const neu = `${uid}-${old}`
    idMap.set(old, neu)
    node.setAttribute('id', neu)
  })

  const rewrite = (val: string) =>
    val.replace(/url\(\s*#([^)]+?)\s*\)/g, (_m, id: string) => {
      const neu = idMap.get(id.trim())
      return neu ? `url(#${neu})` : _m
    })

  clone.querySelectorAll('*').forEach((node) => {
    for (const attr of [
      'mask',
      'clip-path',
      'fill',
      'stroke',
      'filter',
      'href',
      'xlink:href',
      'style'
    ]) {
      const raw = node.getAttribute(attr)
      if (raw && raw.includes('url(')) node.setAttribute(attr, rewrite(raw))
    }
  })

  const xml = new XMLSerializer().serializeToString(clone)
  return {
    ...base,
    url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`
  }
}
